import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { Reducer, Store } from 'redux';

import { createSimpleReducer } from './createSimpleReducer';
import { alertReducer } from '../slices/alertSlice';
import { deleteMessageReducer } from '../slices/deleteMessageSlice';
import { sessionReducer } from '../slices/sessionSlice';
import { socketStatusReducer } from '../slices/socketStatusSlice';
import { userReducer } from '../slices/userSlice';
import { TAppState } from '../types/appState';
import { IApplicationContext } from '../types/applicationContext';
import { TExperimentsState } from '../types/experiments';
import { TReduxActions } from '../types/redux/actions';

interface ICustomReducer extends Reducer<TAppState> {
  addReducer(reducer: Reducer<TAppState>): void;
}

function createCustomReducer(rootReducer: Reducer): ICustomReducer {
  const subReducers: Reducer<TAppState>[] = [];

  const reducer = (state: TAppState, action: TReduxActions) => ({
    ...subReducers.reduce((acc, subReducer) => subReducer(acc, action), state),
    ...rootReducer(state, action),
  });

  reducer.addReducer = (subReducer: Reducer<TAppState>) => {
    subReducers.push(subReducer);
  };

  return reducer;
}

export interface IAppStore extends Store<TAppState> {
  addReducer(reducer: Reducer<TAppState>): void;
  serialize(): TAppState;
}

interface IAppStoreConfig {
  initialState: TAppState;
  optionalParams?: {
    dispatchReducerAddedAction?: boolean;
    dispatchRunnerSagaAction?: boolean;
  };
  context: IApplicationContext;
}

export function createAppStore({ initialState, optionalParams, context }: IAppStoreConfig) {
  const rootReducer = combineReducers({
    alert: alertReducer,
    deleteMessage: deleteMessageReducer,
    experiments: createSimpleReducer<TExperimentsState>('experiments', []),
    session: sessionReducer,
    socketStatus: socketStatusReducer,
    source: createSimpleReducer<string | null>('source', null),
    user: userReducer,
  });
  const customReducer = createCustomReducer(rootReducer);

  const store = configureStore({
    reducer: customReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: { extraArgument: context },
        serializableCheck: {
          ignoredActionPaths: ['payload.messagesMap'],
        },
      }),
    devTools: true,
    preloadedState: initialState,
  });

  function addReducer(reducer: Reducer<TAppState>) {
    if (optionalParams && optionalParams.dispatchReducerAddedAction) {
      store.dispatch({
        name: reducer.name,
        type: '@@store/REDUCER_ADDED',
      });
    }
    customReducer.addReducer(reducer);
  }

  function serialize(): TAppState {
    return store.getState();
  }

  return { ...store, addReducer, serialize };
}
